/**
 * <SlotChangeSeatsStep> — l'étape « Une place chez Camille » de « Modifier mes créneaux » (organism).
 *
 * Le serveur a trouvé, chez les tuteurs des cours qui changent, des cours qui ont encore de la place
 * toutes les semaines. Ils se lisent par tuteur — « Camille a de la place sur ces créneaux, toutes
 * les semaines : » quand il est seul, « Camille a de la place, toutes les semaines : » puis « Yanis
 * aussi : » sinon —, une carte d'option par place : le visage du tuteur, « Lundi · 18h00 », « Maths ·
 * avec Camille · 1 place » (rien à compter sur un cours individuel), et la radio à droite. On en
 * choisit une par cours qui change ; avec plusieurs cours, chaque carte dit lequel elle remplace.
 * Le serveur n'offre des places que s'il en a pour chaque cours : sinon l'étape ne s'ouvre pas.
 *
 * Contrôlé : les choix arrivent (`choices`, cours → place), une place repart par `onSelect`.
 * Pendant une relecture (une place vient d'être prise), les cartes montrent leur forme.
 */
import React from "react"

import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { OptionCardSkeleton } from "../../molecules/OptionCard/OptionCardSkeleton"
import { type UISlotChangeCourse, type UISlotChangeSeat, type UITutor } from "../../../ports/primary/ui/types"
import { subjectSummary, tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { slotChangeCourseLabel } from "../../../ports/primary/ui/formatters/slotChangeFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { formatWeeklySlot } from "../../../ports/primary/ui/formatters/weeklySlotFormatter"

const LEADING_SIZE = 44

export type SlotChangeSeatsStepProps = {
    /** Les cours qui changent, dans l'ordre où le parent les a choisis. */
    courses: UISlotChangeCourse[]
    seats: UISlotChangeSeat[]
    /** Cours qui change → la place choisie pour lui. */
    choices: Record<number, number>
    onSelect: (seat: UISlotChangeSeat) => void
    loading?: boolean
    testID?: string
}

/** Les places rangées par tuteur, dans l'ordre où le serveur les sert. */
function byTutor(seats: UISlotChangeSeat[]): { tutor: UITutor; seats: UISlotChangeSeat[] }[] {
    const groups: { tutor: UITutor; seats: UISlotChangeSeat[] }[] = []
    for (const seat of seats) {
        const group = groups.find((candidate) => String(candidate.tutor.id) === String(seat.tutor.id))
        if (group != null) group.seats.push(seat)
        else groups.push({ tutor: seat.tutor, seats: [seat] })
    }
    return groups
}

/** « Maths · avec Camille · 1 place ». */
function seatDetails(seat: UISlotChangeSeat): string {
    return [
        subjectSummary(seat.subjects),
        seat.tutor.firstname ? `avec ${seat.tutor.firstname}` : "",
        seat.seatsLeft != null ? countLabel(seat.seatsLeft, "place") : "",
    ]
        .filter((part) => part !== "")
        .join(" · ")
}

export function SlotChangeSeatsStep(props: SlotChangeSeatsStepProps) {
    const { courses, seats, choices, onSelect, loading = false, testID } = props

    if (loading) {
        return (
            <DelayedSkeleton accessibilityLabel="Chargement des places">
                <Stack gap="2" testID={testID}>
                    <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={120} descriptionWidth={170} />
                    <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={110} descriptionWidth={150} />
                </Stack>
            </DelayedSkeleton>
        )
    }

    const several = courses.length > 1
    const courseOf = (id: number) => courses.find((course) => course.liveSessionId === id)
    const groups = byTutor(seats)

    return (
        <Stack gap="4" testID={testID}>
            {groups.map((group, index) => {
                const name = group.tutor.firstname || "Son tuteur"
                return (
                    <Stack key={String(group.tutor.id)} gap="2">
                        {index === 0 ? (
                            <Text size="text-md" color="text-strong" style={styles.flex}>
                                {groups.length === 1
                                    ? `${name} a de la place sur ces créneaux, `
                                    : `${name} a de la place, `}
                                <Text size="text-md" weight="bold" color="text-strong">
                                    toutes les semaines
                                </Text>
                                {" :"}
                            </Text>
                        ) : (
                            <Text size="text-md" color="text-strong">{`${name} aussi :`}</Text>
                        )}
                        {group.seats.map((seat) => {
                            const course = courseOf(seat.courseLiveSessionId)
                            return (
                                <OptionCard
                                    key={`${seat.courseLiveSessionId}-${seat.liveSessionId}`}
                                    label={formatWeeklySlot(seat.label, seat.dayTime)}
                                    description={seatDetails(seat)}
                                    note={
                                        several && course != null
                                            ? `À la place : ${slotChangeCourseLabel(course, { inSentence: true })}`
                                            : undefined
                                    }
                                    selected={choices[seat.courseLiveSessionId] === seat.liveSessionId}
                                    control="radio-trailing"
                                    onPress={() => onSelect(seat)}
                                    leading={
                                        <Avatar
                                            uri={seat.tutor.avatarUrl}
                                            name={tutorShortName(seat.tutor)}
                                            size={LEADING_SIZE}
                                            signed
                                        />
                                    }
                                />
                            )
                        })}
                    </Stack>
                )
            })}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    flex: { flexShrink: 1 },
}))
